import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Modal, Upload, UploadFile, UploadProps } from 'antd';
import { AxiosResponse } from 'axios';
import { TbBuilding, TbPhotoUp, TbShieldCheck } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useWorkspaceStore } from '@/stores';
import { SetStateMethod, Workspace } from '@/types';

interface UploadWorkspaceAvatarModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const MAX_AVATAR_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_AVATAR_MIME_TYPES = ['image/jpeg', 'image/png'];

const UploadWorkspaceAvatarModal: FC<UploadWorkspaceAvatarModalProps> = ({ open, setOpen }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { selectedWorkspace: workspace, updateWorkspace } = useWorkspaceStore();

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);

    const previewAvatar = useMemo(() => {
        const file = fileList[0]?.originFileObj;

        if (!file) {
            return workspace?.avatar;
        }

        return URL.createObjectURL(file);
    }, [fileList, workspace?.avatar]);

    useEffect(() => {
        return () => {
            if (previewAvatar?.startsWith('blob:')) {
                URL.revokeObjectURL(previewAvatar);
            }
        };
    }, [previewAvatar]);

    const resetUploadState = () => {
        setFileList([]);
        setOpen(false);
    };

    const beforeUpload: UploadProps['beforeUpload'] = (file) => {
        if (!ACCEPTED_AVATAR_MIME_TYPES.includes(file.type)) {
            toast.error('Workspace avatar must be a JPEG or PNG image.');

            return Upload.LIST_IGNORE;
        }

        if (file.size > MAX_AVATAR_FILE_SIZE_BYTES) {
            toast.error('Workspace avatar must be smaller than 5 MB.');

            return Upload.LIST_IGNORE;
        }

        return false;
    };

    const handleChange: UploadProps['onChange'] = ({ fileList }) => {
        setFileList(fileList.slice(-1));
    };

    const handleUpload = async () => {
        const file = fileList[0]?.originFileObj;

        if (!file || !workspace?.id) {
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            setLoading(true);

            const res: AxiosResponse<{ avatar: string; file: string; resource?: Workspace }> =
                await authorizedAxiosInstance.post(`/api/upload/${workspace.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    params: {
                        type: 'workspace',
                    },
                });

            if (res.status === 200) {
                const nextAvatar = res.data.avatar ?? res.data.file;

                await updateWorkspace(workspace.id, { avatar: nextAvatar });
                resetUploadState();

                toast.success('Workspace Avatar Updated', {
                    icon: <TbShieldCheck size='1rem' />,
                    description: workspace.name,
                });
            }
        } catch (error) {
            captureException(error);
            toast.error('Upload failed', {
                description: 'The image could not be scanned and processed.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            mask={false}
            open={open}
            title='Upload Workspace Avatar'
            width={480}
            onCancel={resetUploadState}
            okText='Save Avatar'
            okButtonProps={{
                disabled: fileList.length < 1,
                loading,
                icon: <TbShieldCheck style={{ verticalAlign: '-0.125rem' }} />,
            }}
            onOk={handleUpload}
        >
            <div style={{ textAlign: 'center', padding: '2rem 0 1rem' }}>
                <Avatar
                    size={96}
                    shape='square'
                    src={previewAvatar || undefined}
                    icon={
                        workspace?.emoji ? (
                            workspace.emoji
                        ) : (
                            <TbBuilding style={{ verticalAlign: '-0.35rem', color: token.colorTextTertiary }} />
                        )
                    }
                    style={{
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                    }}
                />
            </div>
            <Upload.Dragger
                accept={ACCEPTED_AVATAR_MIME_TYPES.join(',')}
                beforeUpload={beforeUpload}
                fileList={fileList}
                maxCount={1}
                multiple={false}
                onChange={handleChange}
                showUploadList={{ showPreviewIcon: false }}
            >
                <p className='ant-upload-drag-icon'>
                    <TbPhotoUp size='1.5rem' style={{ color: token.colorTextTertiary }} />
                </p>
                <p className='ant-upload-text'>Drop an image here or click to browse</p>
                <p className='ant-upload-hint'>JPEG or PNG. Files are scanned before they are stored.</p>
            </Upload.Dragger>
        </Modal>
    );
};

export default UploadWorkspaceAvatarModal;
